import { useMemo } from "react";
import useSWR, { SWRResponse } from "swr";
import type { Contributor } from "@memento/types/contributor";
import { useAuthenticatedFetcher, getLoadingStatus } from "src/libs/fetcher";
import {
  getInvited,
  getEverInvited,
  getUninvited,
  getBounced,
  getTracked,
  sortContributorsByName,
  getSubmitted,
  getPending,
} from "src/libs/contributor-lib";
import { getCardContributorsEndpointUrl } from "src/libs/api-endpoint-lib";

export const useCardContributors = (cardId: string | null) => {
  const fetcher = useAuthenticatedFetcher();
  const key = cardId ? getCardContributorsEndpointUrl(cardId) : null;

  const { data, error, mutate }: SWRResponse<Contributor[], Error> = useSWR(
    key,
    (url: string) => fetcher(url)
  );

  const [
    allContributors,
    uninvitedContributors,
    invitedContributors,
    everInvitedContributors,
    trackedContributors,
    bouncedContributors,
    submittedContributors,
    pendingContributors,
  ] = useMemo(() => {
    const allContributors: Contributor[] = sortContributorsByName(data || []);

    return [
      allContributors,
      getUninvited(allContributors),
      getInvited(allContributors),
      getEverInvited(allContributors),
      getTracked(allContributors),
      getBounced(allContributors),
      getSubmitted(allContributors),
      getPending(allContributors),
    ];
  }, [data]);

  return {
    allContributors,
    uninvitedContributors,
    invitedContributors,
    everInvitedContributors,
    trackedContributors,
    bouncedContributors,
    submittedContributors,
    pendingContributors,
    error,
    status: getLoadingStatus(key, data, error),
    mutate,
  };
};
